/**
 * Combines the response of two separate shopping list/wishlist queries into a single array of items.
 *
 * @param {ShoppinglistVariantOverviewFragment | null} variantData - The variant-specific data from a shopping list query.
 * @param {ShoppinglistProductOverviewFragment | null} productData - The product-specific data from a shopping list query.
 *
 * @returns {CombinedItem[] | null} - An array of combined items, each containing SKU, name, quantity, product, and variant data.
 * Returns null if no items can be combined.
 *
 * @description
 * Due to a known issue with Apollo (https://github.com/apollographql/router/issues/5844),
 * variant and product data are fetched separately using the same query but with different fields to avoid failures.
 *
 * Since the data is resolved asynchronously, the number of items in `variantData` and `productData` can be different
 * (e.g., variantData.length could be 2 while productData.length is 3). To handle this discrepancy,
 * the function iterates over the minimum length of the two arrays, combining their respective items into one array.
 *
 * This function avoids accessing undefined data by ensuring the number of combined items matches the smaller of the two arrays.
 */ const combineWishlistData = (variantData, productData)=>{
    const productItems = (productData === null || productData === void 0 ? void 0 : productData.items) || [];
    const variantItems = (variantData === null || variantData === void 0 ? void 0 : variantData.items) || [];
    const minLength = Math.min(productItems.length, variantItems.length);
    const combinedItems = Array.from({
        length: minLength
    }, (_, index)=>{
        const productItem = productItems[index];
        const variantItem = variantItems[index];
        if (!productItem || !variantItem) {
            return null;
        }
        return {
            sku: productItem.sku,
            name: productItem.name,
            quantity: productItem.quantity,
            product: productItem.product,
            variant: variantItem.variant
        };
    }).filter((item)=>item !== null
    );
    return combinedItems.length ? combinedItems : null;
};

export { combineWishlistData };
//# sourceMappingURL=combine-wishlist-data.js.map
